import {
    assertUnreachable,
    type Notification,
    NotificationResourceType,
    ValidationErrorType,
} from '@lightdash/common';
import { Button, getDefaultZIndex, Indicator, Menu } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import { Fragment, type FC, useMemo } from 'react';
import { useAiAgentOrgPermission } from '../../../ee/features/aiCopilot/hooks/useAiAgentPermission';
import { useContentReviewAvailability } from '../../../ee/features/contentReview/hooks/useContentReviewAvailability';
import { useDashboardCommentsCheck } from '../../../features/comments';
import {
    AiReviewNotifications,
    ContentReviewNotifications,
    DashboardCommentsNotifications,
    useGetNotifications,
} from '../../../features/notifications';
import {
    useValidation,
    useValidationNotificationChecker,
    useValidationUserAbility,
} from '../../../hooks/validation/useValidation';
import useApp from '../../../providers/App/useApp';
import MantineIcon from '../../common/MantineIcon';
import { useNavBarMenuProps } from '../NavBarPortalContext';
import classes from './NotificationsMenu.module.css';
import { ValidationErrorNotification } from './ValidationErrorNotification';

export const NotificationsMenu: FC<{
    projectUuid: string;
    withLabel?: boolean;
}> = ({ projectUuid, withLabel = false }) => {
    const menuProps = useNavBarMenuProps();
    const { user } = useApp();

    // Validator notifications
    const { data: validationData } = useValidation(projectUuid, user, false);
    // Ignore non-blocking chart configuration warnings in the header count
    const validationErrors = validationData?.filter(
        (v) => v.errorType !== ValidationErrorType.ChartConfiguration,
    );
    const canUserManageValidations = useValidationUserAbility(projectUuid);
    const [hasReadValidationNotification, setHasReadValidationNotification] =
        useValidationNotificationChecker();
    const hasValidationNotifications =
        validationErrors && validationErrors.length > 0;

    // Dashboard comments notifications
    const { canViewDashboardComments } = useDashboardCommentsCheck(user?.data);
    const { data: dashboardCommentsNotifications } = useGetNotifications(
        NotificationResourceType.DashboardComments,
        canViewDashboardComments,
    );
    const hasDashboardCommentsNotifications =
        dashboardCommentsNotifications &&
        dashboardCommentsNotifications.length > 0;
    const canViewAiReviews = useAiAgentOrgPermission({ action: 'manage' });
    const { data: aiReviewNotifications } = useGetNotifications(
        NotificationResourceType.AiReview,
        !!canViewAiReviews,
    );
    const hasAiReviewNotifications =
        aiReviewNotifications && aiReviewNotifications.length > 0;
    const { isAvailable: canViewContentReviews } =
        useContentReviewAvailability();
    const { data: contentReviewNotifications } = useGetNotifications(
        NotificationResourceType.ContentReview,
        canViewContentReviews,
    );
    const hasContentReviewNotifications =
        contentReviewNotifications && contentReviewNotifications.length > 0;
    const notifications = useMemo<Notification[]>(
        () =>
            [
                ...(dashboardCommentsNotifications ?? []),
                ...(aiReviewNotifications ?? []),
                ...(contentReviewNotifications ?? []),
            ].sort(
                (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime(),
            ),
        [
            aiReviewNotifications,
            contentReviewNotifications,
            dashboardCommentsNotifications,
        ],
    );

    const showNotificationBadge = () => {
        /**
         * Show notification badge if:
         * - User can manage validations and there are unread validation errors
         * - Feature flag for Dashboard Comments is on and there are unread dashboard comments
         */
        if (canUserManageValidations && hasValidationNotifications) {
            return !hasReadValidationNotification;
        }

        if (canViewDashboardComments) {
            const hasUnreadComments = dashboardCommentsNotifications?.some(
                (n) => !n.viewed,
            );
            if (hasUnreadComments) return true;
        }

        if (canViewAiReviews) {
            const hasUnreadAiReviews = aiReviewNotifications?.some(
                (n) => !n.viewed,
            );
            if (hasUnreadAiReviews) return true;
        }

        if (canViewContentReviews) {
            const hasUnreadContentReviews = contentReviewNotifications?.some(
                (n) => !n.viewed,
            );
            if (hasUnreadContentReviews) return true;
        }

        return false;
    };

    const renderNotification = (notification: Notification) => {
        switch (notification.resourceType) {
            case NotificationResourceType.DashboardComments:
                return (
                    <DashboardCommentsNotifications
                        notifications={[notification]}
                        projectUuid={projectUuid}
                    />
                );
            case NotificationResourceType.AiReview:
                return <AiReviewNotifications notifications={[notification]} />;
            case NotificationResourceType.ContentReview:
                return (
                    <ContentReviewNotifications
                        notifications={[notification]}
                    />
                );
            default:
                return assertUnreachable(
                    notification,
                    'Unknown notification type',
                );
        }
    };

    const shouldDisplayMenu =
        canViewDashboardComments ||
        canUserManageValidations ||
        canViewAiReviews ||
        canViewContentReviews;

    return shouldDisplayMenu ? (
        <Menu
            position="bottom-end"
            arrowOffset={16}
            offset={-2}
            zIndex={getDefaultZIndex('max')}
            {...menuProps}
        >
            <Menu.Target>
                <Button
                    aria-label="Notifications"
                    variant="default"
                    size="xs"
                    // NOTE: Set validation notification as read (Local Storage)
                    onClick={setHasReadValidationNotification}
                    classNames={{ label: classes.buttonLabel }}
                >
                    <Indicator
                        size={12}
                        color="red"
                        offset={1}
                        disabled={!showNotificationBadge()}
                    >
                        <MantineIcon icon={IconBell} />
                    </Indicator>
                    {withLabel && 'Notifications'}
                </Button>
            </Menu.Target>
            <Menu.Dropdown
                maw="min(400px, calc(100vw - 24px))"
                className={classes.dropdown}
            >
                {hasValidationNotifications && (
                    <ValidationErrorNotification
                        projectUuid={projectUuid}
                        validationData={validationErrors}
                    />
                )}
                {notifications.length > 0 && (
                    <>
                        {notifications.map((notification) => (
                            <Fragment key={notification.notificationId}>
                                {renderNotification(notification)}
                            </Fragment>
                        ))}
                    </>
                )}
                {!hasValidationNotifications &&
                    !hasDashboardCommentsNotifications &&
                    !hasAiReviewNotifications &&
                    !hasContentReviewNotifications && (
                        <Menu.Item fz="sm">No notifications</Menu.Item>
                    )}
            </Menu.Dropdown>
        </Menu>
    ) : null;
};
